import { useAtom } from '@reatom/npm-react'
import styled from 'styled-components'

import { CurrencyModel } from '@root/entities/currency'
import { useVisibilityFormatter } from '@root/entities/preferences'
import { AmountFormatted } from '@root/shared/core/format'

import { useWalletAssetBalance, useWalletAssetBalanceUsd } from '../../hooks'
import { WalletModelShort } from '../wallet-short'

import { WalletSelectItem } from './wallet-select-item'

import type { WalletModel } from '../../atoms'
import type { Size } from '@blade/ui-theme'
import type { ClassNameProps } from '@root/shared/react'

export type WalletModelSelectItemProps = {
  active?: boolean
  wallet: WalletModel
  onClick?: React.MouseEventHandler<HTMLElement>
  currency?: CurrencyModel
  size?: Extract<Size, 's' | 'm'>
  selectionMode?: 'check' | 'radio'
} & ClassNameProps

export const WalletModelSelectItem: React.FC<WalletModelSelectItemProps> = ({
  active = false,
  wallet,
  onClick,
  currency,
  size = 'm',
  className,
  selectionMode = 'check',
}) => {
  const [address] = useAtom(wallet.address)

  const currencyResolved = currency
    ? currency
    : CurrencyModel.nativeOf(wallet.network)

  const assetBalance = useWalletAssetBalance({
    currency: currencyResolved,
    wallet,
  })
  const assetBalanceUsd = useWalletAssetBalanceUsd({
    currency: currencyResolved,
    wallet,
  })

  const handleClick: React.MouseEventHandler<HTMLDivElement> = (e) => {
    if (!onClick) {
      return
    }

    onClick(e)
  }

  const formatHidden = useVisibilityFormatter('****')

  const balanceFormatted = formatHidden(
    <>
      <AmountFormatted
        amount={assetBalance}
        currency={currencyResolved.ticker}
        roundingMode="trunc"
      />
    </>,
  )

  return (
    <WalletSelectItem
      active={active}
      onClick={handleClick}
      balanceNativeSlot={balanceFormatted}
      balanceUsd={assetBalanceUsd}
      address={address}
      walletNameSlot={<WalletName wallet={wallet} />}
      size={size}
      className={className}
      selectionMode={selectionMode}
    />
  )
}

const WalletName = styled(WalletModelShort)`
  gap: 2px;
  line-height: 1;

  [data-size='s'] & {
    --wallet-short-indicator-size: 3px;

    font-size: 12px;
    font-weight: 600;
  }
  [data-size='m'] & {
    --wallet-short-indicator-size: 4px;

    font-size: 12px;
    font-weight: 700;
  }
`
